/**
 * 2.0.0: 主题相关的便捷 hook
 *
 * 封装 themeStore，提供更简洁的 API 和辅助函数。
 * 在组件中使用此 hook 而非直接导入 themeStore，
 * 便于后续扩展和统一管理。
 */

import { useCallback } from 'react';
import { useThemeStore, type ThemeMode } from '../stores/themeStore';

/**
 * 主题 hook — 组件中使用的主要接口
 *
 * @returns 主题状态和操作方法
 *
 * 使用示例：
 * ```tsx
 * const { isDark, mode, setMode, cycleMode } = useTheme();
 *
 * // 判断当前是否暗色
 * if (isDark) { ... }
 *
 * // 循环切换: light → dark → system → light
 * <button onClick={cycleMode}>切换主题</button>
 * ```
 */
export const useTheme = () => {
  const mode = useThemeStore((s) => s.mode);
  const resolved = useThemeStore((s) => s.resolved);
  const setMode = useThemeStore((s) => s.setMode);

  /** 当前实际生效的是否为暗色主题 */
  const isDark = resolved === 'dark';

  /**
   * 循环切换主题模式
   * light → dark → system → light
   * 用于 Header 中的主题切换按钮
   */
  const cycleMode = useCallback(() => {
    const nextMap: Record<ThemeMode, ThemeMode> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    };
    setMode(nextMap[mode]);
  }, [mode, setMode]);

  return {
    /** 用户选择的模式 ('light' | 'dark' | 'system') */
    mode,
    /** 实际生效的主题 ('light' | 'dark') */
    resolved,
    /** 是否为暗色主题（便捷判断） */
    isDark,
    /** 设置指定模式 */
    setMode,
    /** 循环切换: light → dark → system */
    cycleMode,
  };
};
